import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import api from '../../services/api'

export type LeaveType = 'sick' | 'casual' | 'paid' | 'unpaid' | 'other'
export type LeaveStatus = 'pending' | 'approved' | 'rejected' | 'cancelled'

export interface LeaveUser {
  _id: string
  name: string
  email: string
  avatar?: string
  role?: string
}

export interface Leave {
  _id: string
  user: string | LeaveUser
  leaveType: LeaveType
  fromDate: string
  toDate: string
  title: string
  description?: string
  status: LeaveStatus
  actionedBy?: string | LeaveUser
  actionedAt?: string
  rejectionReason?: string
  createdAt: string
}

export interface LeaveStats {
  pending: number
  approved: number
  rejected: number
  cancelled: number
}

export interface LeavePagination {
  page: number
  limit: number
  total: number
  pages: number
}

export interface LeaveState {
  myLeaves: Leave[]
  allLeaves: Leave[]
  todayLeaves: Leave[]
  requesters: LeaveUser[]
  stats: LeaveStats | null
  pagination: LeavePagination | null
  loading: boolean
  actionLoading: boolean
  todayLoading: boolean
  error: string | null
}

const initialState: LeaveState = {
  myLeaves: [],
  allLeaves: [],
  todayLeaves: [],
  requesters: [],
  stats: null,
  pagination: null,
  loading: false,
  actionLoading: false,
  todayLoading: false,
  error: null,
}

export const fetchMyLeaves = createAsyncThunk(
  'leaves/fetchMine',
  async (params: { status?: LeaveStatus } | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get('/leaves/mine', { params })
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch leave requests')
    }
  }
)

export const fetchAllLeaves = createAsyncThunk(
  'leaves/fetchAll',
  async (params: { status?: LeaveStatus; userId?: string; leaveType?: LeaveType; fromDate?: string; toDate?: string } | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get('/leaves', { params })
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch leave requests')
    }
  }
)

export const fetchLeaveRequesters = createAsyncThunk(
  'leaves/fetchRequesters',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/leaves/requesters')
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch leave requesters')
    }
  }
)

export const fetchTodayLeaves = createAsyncThunk(
  'leaves/fetchToday',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/leaves/today')
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch today\'s leave')
    }
  }
)

export const fetchLeaveStats = createAsyncThunk(
  'leaves/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/leaves/stats')
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch leave statistics')
    }
  }
)

export const createLeave = createAsyncThunk(
  'leaves/create',
  async (leaveData: { leaveType: LeaveType; fromDate: string; toDate: string; title: string; description?: string }, { rejectWithValue }) => {
    try {
      const response = await api.post('/leaves', leaveData)
      return response.data.data.leave
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to submit leave request')
    }
  }
)

export const approveLeave = createAsyncThunk(
  'leaves/approve',
  async ({ leaveId }: { leaveId: string }, { rejectWithValue }) => {
    try {
      await api.put(`/leaves/${leaveId}/approve`)
      return { leaveId, status: 'approved' as LeaveStatus }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to approve leave request')
    }
  }
)

export const rejectLeave = createAsyncThunk(
  'leaves/reject',
  async ({ leaveId, rejectionReason }: { leaveId: string; rejectionReason: string }, { rejectWithValue }) => {
    try {
      await api.put(`/leaves/${leaveId}/reject`, { rejectionReason })
      return { leaveId, status: 'rejected' as LeaveStatus, rejectionReason }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reject leave request')
    }
  }
)

export const cancelLeave = createAsyncThunk(
  'leaves/cancel',
  async ({ leaveId }: { leaveId: string }, { rejectWithValue }) => {
    try {
      await api.put(`/leaves/${leaveId}/cancel`)
      return { leaveId, status: 'cancelled' as LeaveStatus }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to cancel leave request')
    }
  }
)

const leaveSlice = createSlice({
  name: 'leaves',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    addLiveLeave: (state, action: PayloadAction<Leave>) => {
      const exists = state.allLeaves.some((l) => l._id === action.payload._id)
      if (!exists) {
        state.allLeaves.unshift(action.payload)
        if (state.stats) state.stats.pending += 1
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch my leaves
      .addCase(fetchMyLeaves.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchMyLeaves.fulfilled, (state, action) => {
        state.loading = false
        state.myLeaves = action.payload.leaves
        state.pagination = action.payload.pagination
      })
      .addCase(fetchMyLeaves.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

      // Fetch all leaves (admin/manager)
      .addCase(fetchAllLeaves.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAllLeaves.fulfilled, (state, action) => {
        state.loading = false
        state.allLeaves = action.payload.leaves
        state.pagination = action.payload.pagination
      })
      .addCase(fetchAllLeaves.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

      // Fetch requesters (History tab's user filter dropdown) - fails
      // silently, same reasoning as fetchTodayLeaves below.
      .addCase(fetchLeaveRequesters.fulfilled, (state, action) => {
        state.requesters = action.payload.users
      })

      // Fetch today's leave (dashboard widget - own loading flag so it never
      // shows/hides the LeavesPage table spinner or vice versa)
      .addCase(fetchTodayLeaves.pending, (state) => {
        state.todayLoading = true
      })
      .addCase(fetchTodayLeaves.fulfilled, (state, action) => {
        state.todayLoading = false
        state.todayLeaves = action.payload.leaves
      })
      .addCase(fetchTodayLeaves.rejected, (state) => {
        state.todayLoading = false
      })

      // Fetch stats
      .addCase(fetchLeaveStats.pending, (state) => {
        state.actionLoading = true
        state.error = null
      })
      .addCase(fetchLeaveStats.fulfilled, (state, action) => {
        state.actionLoading = false
        state.stats = action.payload
      })
      .addCase(fetchLeaveStats.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload as string
      })

      // Create leave
      .addCase(createLeave.pending, (state) => {
        state.actionLoading = true
        state.error = null
      })
      .addCase(createLeave.fulfilled, (state, action) => {
        state.actionLoading = false
        state.myLeaves.unshift(action.payload)
      })
      .addCase(createLeave.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload as string
      })

      // Approve leave
      .addCase(approveLeave.pending, (state) => {
        state.actionLoading = true
        state.error = null
      })
      .addCase(approveLeave.fulfilled, (state, action) => {
        state.actionLoading = false
        const { leaveId, status } = action.payload
        const leave = state.allLeaves.find((l) => l._id === leaveId)
        if (leave) leave.status = status
        if (state.stats) {
          state.stats.pending = Math.max(0, state.stats.pending - 1)
          state.stats.approved += 1
        }
      })
      .addCase(approveLeave.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload as string
      })

      // Reject leave
      .addCase(rejectLeave.pending, (state) => {
        state.actionLoading = true
        state.error = null
      })
      .addCase(rejectLeave.fulfilled, (state, action) => {
        state.actionLoading = false
        const { leaveId, status, rejectionReason } = action.payload
        const leave = state.allLeaves.find((l) => l._id === leaveId)
        if (leave) {
          leave.status = status
          leave.rejectionReason = rejectionReason
        }
        if (state.stats) {
          state.stats.pending = Math.max(0, state.stats.pending - 1)
          state.stats.rejected += 1
        }
      })
      .addCase(rejectLeave.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload as string
      })

      // Cancel leave
      .addCase(cancelLeave.pending, (state) => {
        state.actionLoading = true
        state.error = null
      })
      .addCase(cancelLeave.fulfilled, (state, action) => {
        state.actionLoading = false
        const { leaveId, status } = action.payload
        const leave = state.myLeaves.find((l) => l._id === leaveId)
        if (leave) leave.status = status
      })
      .addCase(cancelLeave.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload as string
      })
  },
})

export const { clearError, addLiveLeave } = leaveSlice.actions
export default leaveSlice.reducer
